import {
  Button,
  Page,
  PageBody,
  Result,
  Stack,
} from '@pypestream/design-system';
import { isIgnoredError, reportError } from '@pypestream/utils';
import { FC, useEffect, useRef } from 'react';
import {
  ErrorBoundary as ReactErrorBoundary,
  useErrorBoundary,
} from 'react-error-boundary';

import { logError } from './helpers';
import { ChildrenProps, ErrorFlags, ErrorPageLabels, ErrorTags } from './types';

/** What the listener layer needs: `labels` are only read by the fallback page. */
interface UncaughtErrorBoundaryProps extends ChildrenProps {
  /** Errors the app's state machine is currently holding. */
  errors?: Error[] | null;
  flags: ErrorFlags;
  /** Reporting is skipped in development, as it was in both apps. */
  isDevelopment: boolean;
}

interface AppErrorBoundaryProps extends UncaughtErrorBoundaryProps {
  labels: ErrorPageLabels;
}

const UncaughtErrorBoundary: FC<UncaughtErrorBoundaryProps> = ({
  children,
  errors: machineErrors,
  flags,
  isDevelopment,
}) => {
  const { isLocalError, isGlobalError, isXstateError } = flags;
  const errors = !isLocalError ? machineErrors : null;
  const { showBoundary } = useErrorBoundary();

  const handler = (e: ErrorEvent) => {
    // Benign browser notification, not a real error — it must not throw the
    // user onto an error page. Same rule Sentry filters on, read from one place.
    if (isIgnoredError(e.message ?? '')) {
      return;
    }

    showBoundary(e);
  };

  const removeErrorListener = () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('error', handler);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isLocalError || isXstateError) {
      removeErrorListener();

      return;
    }

    // Only `error`. An `unhandledrejection` listener that calls `showBoundary`
    // replaces the whole app with the crash page for a rejection the user's own
    // work never depended on — and the rejections that actually occur come from
    // third-party scripts: the Produktly SDK throws `ReferenceError: process is
    // not defined` on the first interaction, which took down Manager's preview
    // on every click of the avatar (E2E `Check My Account Page`, PSTUDIO-4190).
    //
    // Nothing is lost by not listening: Sentry reports these itself through the
    // default `globalHandlersIntegration` — issue MANAGER-AB carries that exact
    // rejection with `mechanism: auto.browser.global_handlers.onunhandledrejection`,
    // and it predates this package by six days. So this listener only ever
    // decided whether to show the crash page, never whether to report.
    window.addEventListener('error', handler);

    return () => {
      removeErrorListener();
    };
    // `isXstateError` is read in the body above: without it here, entering an
    // xstate error does not re-run the effect and the listeners stay attached,
    // which is the opposite of what the branch says it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalError, isXstateError]);

  useEffect(() => {
    if (
      (isGlobalError || isLocalError || isXstateError) &&
      machineErrors &&
      !isDevelopment
    ) {
      machineErrors.forEach((error) => {
        reportError(error, {
          source: 'UncaughtErrorBoundary',
          type: isGlobalError
            ? 'GlobalError'
            : isLocalError
              ? 'LocalError'
              : isXstateError
                ? 'XstateError'
                : 'UnknownError',
          tags: {
            ...(isXstateError && { [ErrorTags.xstate]: String(isXstateError) }),
            ...(isLocalError && { [ErrorTags.local]: String(isLocalError) }),
            ...(isGlobalError && { [ErrorTags.global]: String(isGlobalError) }),
          },
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isGlobalError,
    isLocalError,
    isXstateError,
    machineErrors,
    isDevelopment,
  ]);

  useEffect(() => {
    if (!isGlobalError || !errors || errors.length === 0) {
      return;
    }

    showBoundary(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalError, errors]);

  return <>{children}</>;
};

interface ErrorPageProps {
  error: Error | [Error];
  resetErrorBoundary: () => void;
  labels: ErrorPageLabels;
}

/**
 * Declared at module level, not inside the boundary: a component defined during
 * render has a fresh identity every pass, which remounts the whole fallback.
 */
const ErrorPage: FC<ErrorPageProps> = ({
  error,
  resetErrorBoundary,
  labels,
}) => {
  const alertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    alertRef.current?.focus();
  }, []);
  return (
    <div ref={alertRef} tabIndex={-1} role="alert" style={{ outline: 'none' }}>
      <Page background="secondary">
        <PageBody background="none">
          <Result
            align="center"
            status="error"
            labelTag="h1"
            label={Array.isArray(error) ? error[0].message : error.message}
            body={labels.body}
          >
            <Stack gutter="xsmall" justifyContent="center" slot="footer">
              <Button
                size="large"
                variant="secondary"
                onClick={resetErrorBoundary}
              >
                {labels.reload}
              </Button>
              <Button size="large" href="/">
                {labels.home}
              </Button>
            </Stack>
          </Result>
        </PageBody>
      </Page>
    </div>
  );
};

/**
 * The error boundary both apps render at their root.
 *
 * Everything app-specific arrives as a prop — the machine's errors, the flags
 * each app resolves from its own XState hook, and the already-translated
 * labels. That is what lets one implementation serve both: the package holds no
 * user-facing text and knows nothing about either machine.
 */
export const AppErrorBoundary: FC<AppErrorBoundaryProps> = ({
  children,
  labels,
  ...rest
}) => {
  const resetApp = () => {
    window.location.reload();
  };

  return (
    <ReactErrorBoundary
      fallbackRender={(props) => <ErrorPage {...props} labels={labels} />}
      onError={logError}
      onReset={resetApp}
    >
      <UncaughtErrorBoundary {...rest}>{children}</UncaughtErrorBoundary>
    </ReactErrorBoundary>
  );
};
